// One x-axis range shared by a set of stacked Highcharts instances: the navigator on whichever chart
// carries it sets the window on all of them, so two range strips over one subject cannot be dragged into
// stating two different windows.
//
// Holds the rendered instances too, because mirroring a drag means reaching a chart the caller only ever
// saw once as a `ready` event - and a destroyed, not-yet-replaced one must not be called into.

import type { Chart, ExtremesObject } from 'highcharts';

/**
 * The `setExtremes` event as this composable reads it - narrowed to the fields required rather than taken
 * as `any`, so a rename in a Highcharts upgrade fails to compile instead of silently degrading the stack
 * to "never mirrors a drag".
 */
type SetExtremesEvent = Partial<ExtremesObject> & { trigger?: string };

/**
 * The shared range over `ids` - the caller's own names for the charts in the stack. Each chart registers
 * itself as it renders and declares `onSetExtremes` as its x-axis' handler.
 *
 * A plain Map rather than a ref: nothing is rendered from the instances, and a Highcharts instance inside
 * a `ref` would have Vue walking that whole object graph on every mutation.
 */
export function useSyncedChartExtremes<Id extends string>(ids: readonly Id[]) {
    const instances = new Map<Id, Chart>();

    /**
     * A destroyed chart keeps its object but loses its axes, and the registry holds such an entry until a
     * recreate renders the replacement. Every read goes through here, so a stale entry is dropped rather
     * than called into.
     */
    function liveChart(id: Id): Chart | null {
        const instance = instances.get(id);
        if (!instance?.xAxis?.length) {
            instances.delete(id);
            return null;
        }
        return instance;
    }

    /** The rendered instance, on first render and again on every recreate. */
    function register(id: Id, instance: Chart): void {
        instances.set(id, instance);
    }

    // Set while a mirrored write is in flight: the setExtremes below fires this very handler on the
    // other charts, and without the guard the stack redraws itself in a loop.
    let isSyncing = false;

    function setRange(id: Id, min: number | undefined, max: number | undefined): void {
        const instance = liveChart(id);
        if (instance === null) return;

        instance.xAxis[0]?.setExtremes(min, max);
        instance.redraw();
    }

    /**
     * One chart's x-axis `setExtremes` handler, mirrored onto the others. Only a navigator drag
     * propagates: a programmatic write, a data update or a reset each already reaches every chart on
     * its own, and mirroring those as well is how a redraw loop starts.
     */
    function onSetExtremes(event: SetExtremesEvent, source: Id): void {
        if (event.trigger !== 'navigator' || isSyncing) return;

        isSyncing = true;
        for (const id of ids) if (id !== source) setRange(id, event.min, event.max);
        isSyncing = false;
    }

    /**
     * Back to the window every chart opened on. Undefined extremes hand each axis back to its own
     * configured max and its own data minimum, rather than to a range remembered from a drag.
     */
    function reset(): void {
        isSyncing = true;
        for (const id of ids) setRange(id, undefined, undefined);
        isSyncing = false;
    }

    return { register, liveChart, onSetExtremes, reset };
}
